import { forwardRef, useState, type UIEventHandler } from 'react';
import { History, Sparkles } from 'lucide-react';
import { Message } from './Message';

/** Starter commands shown on the empty conversation. Each is clickable and runs
 * the command; the hint tells a clinician what it returns before they commit. */
const EMPTY_STATE_COMMANDS: { cmd: string; hint: string }[] = [
  { cmd: '/summary', hint: 'condition & med overview' },
  { cmd: '/conditions', hint: 'active problem list' },
  { cmd: '/medications', hint: 'current medications' },
  { cmd: '/assessment', hint: 'clinical assessment' },
];

/**
 * UX-07: long clinical sessions used to mount EVERY message (markdown, cards,
 * thinking panels) at once. Only the most recent window renders by default;
 * earlier turns sit behind an explicit, counted control. Windowing (instead of
 * scroll-virtualization) keeps streaming auto-scroll and dynamic message
 * heights untouched — the parent is keyed by thread id, so the window resets
 * per conversation.
 */
const RECENT_MESSAGE_WINDOW = 30;

interface TranscriptProps {
  messages: any[];
  density: string;
  showEvidence: boolean;
  isCommandLoading?: boolean;
  pendingCommand?: string | null;
  onScroll?: UIEventHandler<HTMLDivElement>;
  onRetryCommand?: (command: string) => void;
  onSendSuggestion?: (message: string) => void;
  /** Re-send the prior user turn with options so the server runs the clinical quality loop. */
  onClinicalQualityRerun?: (userText: string) => void;
  onSavePrompt?: (title: string, prompt: string) => void;
  onRemoveSavedPrompt?: (prompt: string) => void;
  isPromptSaved?: (prompt: string) => boolean;
}

export const Transcript = forwardRef<HTMLDivElement, TranscriptProps>(
  (
    {
      messages,
      density,
      showEvidence,
      isCommandLoading,
      pendingCommand,
      onScroll,
      onRetryCommand,
      onSendSuggestion,
      onClinicalQualityRerun,
      onSavePrompt,
      onRemoveSavedPrompt,
      isPromptSaved,
    },
    ref
  ) => {
    const [showEarlier, setShowEarlier] = useState(false);
    const windowed = !showEarlier && messages.length > RECENT_MESSAGE_WINDOW;
    const visibleMessages = windowed ? messages.slice(-RECENT_MESSAGE_WINDOW) : messages;
    const hiddenCount = messages.length - visibleMessages.length;
    // Index offset so the rerun-text lookup can walk the FULL history even
    // when the assistant turn sits at the window edge.
    const baseIndex = messages.length - visibleMessages.length;
    return (
      <div
        ref={ref}
        data-testid="transcript"
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-background px-4 py-3 sm:px-6 sm:py-4"
      >
        <div className="mx-auto w-full min-w-0 max-w-7xl space-y-4 sm:space-y-5">
          {messages.length === 0 && (
            <div className="border-b border-border pb-6 pt-5 text-left">
              <div className="max-w-3xl">
                <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
                  <Sparkles className="size-3.5" aria-hidden />
                  Clinical output
                </div>
                <p className="mt-2 text-[15px] font-medium text-foreground">Ask about this patient</p>
                <p className="mt-1 text-[13px] leading-relaxed text-text-tertiary">
                  Type a clinical question, or start with a command:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {EMPTY_STATE_COMMANDS.map(({ cmd, hint }) => (
                    <button
                      key={cmd}
                      type="button"
                      onClick={() => onSendSuggestion?.(cmd)}
                      disabled={!onSendSuggestion}
                      className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-sunken px-3 py-1.5 text-left transition-colors hover:border-primary/40 hover:bg-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none"
                    >
                      <code className="font-mono text-[12px] font-medium text-foreground">{cmd}</code>
                      <span className="text-[11px] text-text-tertiary">{hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {windowed && (
            <button
              type="button"
              onClick={() => setShowEarlier(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-surface-sunken/20 px-3 py-2.5 text-[13px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken/40 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <History className="size-4" aria-hidden="true" />
              Show {hiddenCount} earlier {hiddenCount === 1 ? 'message' : 'messages'}
            </button>
          )}
          {visibleMessages.map((message, index) => {
            let clinicalQualityRerunUserText: string | undefined;
            if (message.role === 'assistant') {
              for (let i = baseIndex + index - 1; i >= 0; i--) {
                const prev = messages[i];
                if (prev?.role === 'user') {
                  clinicalQualityRerunUserText = (prev.content ?? prev.answer ?? '') as string;
                  break;
                }
              }
            }
            return (
              <div key={message.id} className="min-w-0">
                <Message
                  message={message}
                  density={density}
                  showEvidence={showEvidence}
                  onRetryCommand={message.retryCommand ? onRetryCommand : undefined}
                  onSendSuggestion={message.role === 'assistant' ? onSendSuggestion : undefined}
                  clinicalQualityRerunUserText={clinicalQualityRerunUserText}
                  onClinicalQualityRerun={message.role === 'assistant' ? onClinicalQualityRerun : undefined}
                  onSavePrompt={onSavePrompt}
                  onRemoveSavedPrompt={onRemoveSavedPrompt}
                  isPromptSaved={isPromptSaved}
                />
              </div>
            );
          })}
          {isCommandLoading && (
            <div
              data-testid="message-loading"
              className="flex items-center gap-3 rounded-lg border border-border bg-surface-sunken px-3.5 py-3"
            >
              <span className="thinking-pulse-dot" />
              <span className="text-sm font-medium text-text-tertiary">
                {pendingCommand ? `Working on: ${pendingCommand.split(/\s+/).slice(0, 5).join(' ')}…` : 'Working…'}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

Transcript.displayName = 'Transcript';
